import SectionPanel from '@/components/SectionPanel';
import { useOrganization } from "src/auth/AuthContext";
import { Box, SimpleGrid, Skeleton, Stack, Text, useDisclosure } from "@chakra-ui/react";
import type { DataTableCellContext as CellContext, DataTableColumnDef as ColumnDef } from "src/components/DataTable";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { LuArrowLeftRight, LuCalendarDays, LuCalendarX, LuGoal } from "react-icons/lu";
import type { IconType } from "react-icons";
import { FundraisingYear } from "src/api/FundraisingYear";
import Content from "src/components/Content";
import {
    Select as ReactSelect
} from "chakra-react-select";
import DataTable, { DataTableToolbar, amountHeader, amountCell, amountText } from "src/components/DataTable";
import { ProgressBarReact } from "src/components/ProgressBar";
import { AmountUtil } from "src/data/AmountUtil";
import { reportService } from "src/service/ReportService";
import { useFundraisingProjectNames } from "@/hooks/useProjectNames";
import DashboardFundraisingDialog from "./DashboardFundraisingDialog";
import { MoneyUtil } from "src/data/MoneyUtil";
import { FundraisingCoverage } from "src/api/FundraisingCoverage";
import { Project } from "src/api/Project";
import WaitingSpinner from "@/components/WaitingSpinner";
import StatCard from "@/components/StatCard";
import { formatPicker } from "@/data/DateTimeUtil";
import { useI18n } from '@/i18n/I18nContext';

type FundraisingStat = {
    key: string;
    label: string;
    value: string;
    helpText: ReactNode;
    icon: IconType;
    accentColor: string;
    iconBg: string;
};

const DashboardFundraisingProject = () => {
    const { t } = useI18n();
    const { id } = useParams();
    const organization = useOrganization();
    const homeCurrency = organization?.currency;
    //console.log(`project id=${id}`);

    const [projectId, setProjectId] = useState<number | null>(id ? parseInt(id) : null);
    const [year, setYear] = useState<number | null>(null); // used to know the year in the details dialog
    const [instituteOnly, setInstituteOnly] = useState<boolean>(false); // used to know the column in the details dialog (institute only)
    const { options: projectOptions } = useFundraisingProjectNames();
    const [data, setData] = useState<FundraisingYear[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    //const [currentYearCoverage, setCurrentYearCoverage] = useState<FundraisingCoverage | null>(null); // of current year
    const [overallCoverage, setOverallCoverage] = useState<FundraisingCoverage | null>(null); // of overall
    const [project, setProject] = useState<Project | null>(null); // project details with start and eventually end date

    const currentYear = new Date().getFullYear();

    const { open: isEntriesOpen, onOpen: onEntriesOpen, onClose: onEntriesClose } = useDisclosure();

    const FundraisingSummaryCards = () => {
        const stats: FundraisingStat[] = [
            // {
            //     key: 'current-year',
            //     label: 'Jahresdeckung',
            //     value: homeCurrency ? MoneyUtil.format({ value: currentYearCoverage?.coverage.total ?? 0, currency: homeCurrency }) : "—",
            //     helpText: (
            //         <Stack gap={1}>
            //             {ProgressBarReact(currentYearCoverage?.coverage.percent ?? 0)}
            //             <Text as='span' fontSize='xs'>Jahresdeckung für {currentYear}</Text>
            //         </Stack>
            //     ),
            //     icon: LuChartColumnIncreasing,
            //     accentColor: 'blue.600',
            //     iconBg: 'blue.50',
            // },
            {
                key: 'overall',
                label: 'Gesamtdeckung BUD WK',
                value: homeCurrency ? MoneyUtil.format({ value: overallCoverage?.coverage.total ?? 0, currency: homeCurrency }) : "—",
                helpText: (
                    <Stack gap={1}>
                        {ProgressBarReact(overallCoverage?.coverage.percent ?? 0)}
                        <Text as='span' fontSize='xs'>Gesamtdeckung</Text>
                    </Stack>
                ),
                icon: LuGoal,
                accentColor: 'teal.600',
                iconBg: 'teal.50',
            },
            {
                key: 'overall-average-rate',
                label: 'Gesamtdeckung Ø WK',
                value: homeCurrency ? MoneyUtil.format({ value: overallCoverage?.averageRateOpen.total ?? 0, currency: homeCurrency }) : "—",
                helpText: (
                    <Stack gap={1}>
                        {ProgressBarReact(overallCoverage?.averageRateOpen.percent ?? 0)}
                        <Text as='span' fontSize='xs'>Gesamtdeckung</Text>
                    </Stack>
                ),
                icon: LuArrowLeftRight,
                accentColor: 'blue.600',
                iconBg: 'blue.50',
            },
            {
                key: 'start-date',
                label: 'Anfangsdatum',
                value: project?.startDate ? formatPicker(project.startDate) : '-',
                helpText: 'Projektanfang',
                icon: LuCalendarDays,
                accentColor: 'orange.600',
                iconBg: 'orange.50',
            },
            {
                key: 'end-date',
                label: 'Enddatum',
                value: project?.endDate ? formatPicker(project.endDate) : '-',
                helpText: 'Leer für laufendes Projekt',
                icon: LuCalendarX,
                accentColor: 'purple.600',
                iconBg: 'purple.50',
            },
        ];

        return (
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} gap='4' w='full'>
                {stats.map((stat) => (
                    <StatCard
                        key={stat.key}
                        label={stat.label}
                        value={stat.value}
                        helpText={stat.helpText}
                        icon={stat.icon}
                        accentColor={stat.accentColor}
                        iconBg={stat.iconBg}
                        valueAlign='end'
                        valueFontSize='xl'
                        isLoading={isLoading || !homeCurrency}
                    />
                ))}
            </SimpleGrid>
        );
    };

    const loadProjectData = (projectId: number): Promise<void> => {
        setIsLoading(true);
        return reportService.fundraisingProject(projectId).then(r => {
            let total: FundraisingYear = {
                key: {
                    projectId: projectId,
                    year: undefined
                },
                balance: r.overall.balance,
                coverage: r.overall.coverage
            }
            // overall aggregations with every year
            setData(r.results.concat(total));
            //setCurrentYearCoverage(r.results.find(a => a.key.year === currentYear)?.coverage);
            setOverallCoverage(r.overall.coverage);
            setProject(r.project);
        }).finally(() => setIsLoading(false));
    }

    useEffect(() => {
        if (projectId) {
            loadProjectData(projectId);
        }
    }, [projectId])

    const amountCell2 = (showSign = false) => (info: CellContext<FundraisingYear, number>) => amountCell<FundraisingYear>({ width: '7rem', fontWeight: info.row.original.key.year ? 'normal' : 'extrabold', showSign })(info)

    const amountCellRedIfLate = () => (info: CellContext<FundraisingYear, number>) => {
        let v = info.getValue();
        if (info.row.original.key.year < currentYear && v > 0) {
            return amountText(AmountUtil.format(v), '7rem', 'bold', 'red.500');
        }
        return amountText(AmountUtil.format(info.getValue()), '7rem', info.row.original.key.year ? 'normal' : 'extrabold')
    }

    const columns: ColumnDef<FundraisingYear>[] = useMemo(
        () => [
            {
                id: 'year',
                header: 'Mitteljahr',
                accessorFn: (row: FundraisingYear) => row.key.year,
                cell: (info: CellContext<FundraisingYear, number>) => {
                    let year = info.getValue();
                    return (<Text width='3rem' fontWeight='bold'>{year ?? 'Total'}</Text>)
                },
            },
            {
                id: 'budget',
                header: amountHeader({ name: ['Fundraising Budget', '(BUD WK)'], fontWeight: 'normal', width: '7rem', casing: 'none', fontSize: 'xs' }),
                accessorKey: 'balance.budgetTotal',
                cell: amountCell2(),
                enableSorting: false,
            },
            {
                id: 'institute', // click checked on this id, in this case the details dialog shows the institues only
                header: amountHeader({ name: 'Institutionelle Beiträge', fontWeight: 'normal', width: '7rem', casing: 'none', fontSize: 'xs' }),
                accessorKey: 'balance.instituteTotal',
                cell: amountCell2(),
                enableSorting: false,
            },
            {
                id: 'commitment',
                header: amountHeader({ name: 'Institutionelle Zusagen', fontWeight: 'normal', width: '7rem', casing: 'none', fontSize: 'xs' }),
                accessorKey: 'balance.commitmentTotal',
                cell: amountCellRedIfLate(),
                enableSorting: false,
            },
            // {
            //     id: 'nonInstitute',
            //     header: amountHeader({ name: 'Eigenmittel', fontWeight: 'normal', width: '7rem', casing: 'none', fontSize: 'xs', explanation: 'Einzelspenden + Patenschaften + Übertrag Eigenmittel CO-OPERAID + Übertrag Eigenmittel beim Partner' }),
            //     // Eigenmittel = Eigenmittel + Startcapital + Ubertrag
            //     accessorFn: (row: FundraisingYear) => row.balance.nonInstituteTotal + row.balance.fundMoveTotal + row.balance.transferMoveTotal,
            //     cell: amountCell2(),
            //     enableSorting: false,
            // },
            // {
            //     id: 'fundMove',
            //     header: amountHeader({ name: 'Startkapital', fontWeight: 'normal', width: '7rem', casing: 'none', fontSize: 'xs' }),
            //     accessorKey: 'balance.fundMoveTotal',
            //     cell: amountCell2(),
            //     enableSorting: false,
            // },
            // {
            //     id: 'transferMove',
            //     header: amountHeader({ name: 'Übertrag Partner', fontWeight: 'normal', width: '7rem', casing: 'none', fontSize: 'xs' }),
            //     accessorKey: 'balance.transferMoveTotal',
            //     cell: amountCell2(),
            //     enableSorting: false,
            // },
            {
                id: 'income',
                header: amountHeader({ name: 'Total Mittel', fontWeight: 'normal', width: '7rem', casing: 'none', fontSize: 'xs', explanation: ['Total Mittel = Total institutionelle Beiträge + Zusagen', '*** (bis 31.12.2025 inklusiv Eigenmittel + Übertrag aus Vorgängerprojekt)'] }),
                accessorKey: 'coverage.incomeTotal',
                cell: amountCell2(),
                enableSorting: false,
            },
            {
                id: 'coverage',
                header: amountHeader({ name: ['Offen Total', '(BUD WK)'], fontWeight: 'normal', width: '7rem', casing: 'none', fontSize: 'xs', explanation: 'Jahresdeckung = Total Mittel Laufjahr - Jahres Fundraising Budget (BUD WK)' }),
                accessorKey: 'coverage.coverage.total',
                cell: amountCell2(true),
                enableSorting: false,
            },
            {
                id: 'coveragePercent',
                header: amountHeader({ name: ['Gesamtdeckung', '(BUD WK)'], fontWeight: 'normal', width: '7rem', casing: 'none', fontSize: 'xs', explanation: 'Jahresdeckung % = Total Mittel Laufjahr / Fundraising Budget Laufjahr (BUD WK)' }),
                accessorKey: 'coverage.coverage.percent',
                cell: (info: CellContext<FundraisingYear, number>) => ProgressBarReact(info.getValue(), info.row.original.key.year ? 'normal' : 'bold'),
                enableSorting: false,
            },
            {
                id: 'openAvgRate',
                header: amountHeader({ name: ['Offen Total', '(Ø WK)'], fontWeight: 'normal', width: '8rem', casing: 'none', fontSize: 'xs', explanation: 'Offener Betrag = Total Mittel Laufjahr - Jahres Fundraising Budget (Ø WK)' }),
                accessorKey: 'coverage.averageRateOpen.total',
                cell: amountCell2(true),
                enableSorting: false,
            },
            {
                id: 'overallCoveragePercentAvgRate',
                header: amountHeader({ name: ['Gesamtdeckung', '(Ø WK)'], fontWeight: 'normal', width: '7rem', casing: 'none', fontSize: 'xs', explanation: 'Gesamtdeckung % = Total Mittel Laufjahr / Jahres Fundraising Budget (Ø WK)'}),
                accessorKey: 'coverage.averageRateOpen.percent',
                cell: (info: CellContext<FundraisingYear, number>) => ProgressBarReact(info.getValue(), info.row.original.key.year ? 'normal' : 'bold'),
                enableSorting: false,
            },
        ], []
    );

    if (!homeCurrency) return <WaitingSpinner role="status" />;

    return (
        <Content title={t('reports.fundraisingProject.title')} description={t('reports.fundraisingProject.description')}>
            <Stack gap='6' minW='0'>
                <DataTableToolbar>
                    <Box w={{ base: 'full', md: '20rem' }} minW='0'>
                        <ReactSelect
                            isSearchable
                            required
                            isDisabled={false}
                            autoFocus={false}
                            name='project-select'
                            aria-label={t('projects.details.tab')}
                            placeholder={t('projects.details.tab')}
                            options={projectOptions}
                            onChange={val => setProjectId(val?.value ?? null)}
                            value={projectOptions.find(c => c.value === projectId) ?? null}
                        />
                    </Box>
                </DataTableToolbar>
                {projectId ? <>
                    <FundraisingSummaryCards />

                    {isLoading ? (
                        <Stack py={5}>
                            <WaitingSpinner size='xl' />
                            <Skeleton height='20px' />
                            <Skeleton height='20px' />
                            <Skeleton height='20px' />
                        </Stack>
                    ) : (
                        <SectionPanel title={t('reports.fundraisingProject.coverageByYear')}>
                            <DataTable columns={columns} data={data} initialSortByColumn='year' initialSortByDesc={false}
                                hideColumnsOnXs={['institute', 'nonInstitute', 'commitment', 'fundMove', 'transferMove', 'income', 'coverage', 'coveragePercent', 'openAvgRate']}
                                onRowClick={(row, columnId) => {
                                    setYear(row.key.year);
                                    setInstituteOnly(columnId === 'institute');
                                    console.log(`selected year is ${row.key.year}`);
                                    onEntriesOpen();
                                }}
                            />
                        </SectionPanel>
                    )}
                </> : <Text color='fg.muted' textAlign='center' py='6'>{t('projects.report.selectProject')}</Text>}
            </Stack>
            <DashboardFundraisingDialog projectId={projectId} projectName={projectOptions.find(c => c.value === projectId)?.label}
                projectYear={year}
                instituteOnly={instituteOnly}
                isOpen={isEntriesOpen} onClose={onEntriesClose} />
        </Content>
    );
}

export default DashboardFundraisingProject
